import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DesktopHeaderComponent } from '@core/components/desktop-header/desktop-header.component';
import { SidebarComponent } from '@core/components/sidebar/sidebar.component';
import { MobileHeaderComponent } from '@core/components/mobile-header/mobile-header.component';
import { Store } from '@ngrx/store';
import { userFeature } from '@state';
import { CommonModule, NgIf } from '@angular/common';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AppRouting } from '@core/enums';
import { concatLatestFrom } from '@ngrx/operators';
import { ModalService, StatusMessageService } from '@core/services';
import { FooterComponent, LocaleService } from '@selfcare/cms';
import {
  ActivateSimcardModalComponent,
  PendingPaymentModalComponent,
  UnpaidInvoiceModalComponent,
} from '@shared/components';
import { LoaderComponent } from '@selfcare/ui';
import { toSignal } from '@angular/core/rxjs-interop';
import { subscriptionFeature } from '@src/state/subscription';
import {
  StatusMessageType,
  StatusMessageWidgetComponent,
} from '@shared/components/status-message-widget/status-message-widget.component';

@UntilDestroy()
@Component({
  selector: 'sc-layout',
  imports: [
    RouterOutlet,
    DesktopHeaderComponent,
    FooterComponent,
    SidebarComponent,
    MobileHeaderComponent,
    NgIf,
    CommonModule,
    LoaderComponent,
    StatusMessageWidgetComponent,
  ],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  public currentUser = this.store.selectSignal(userFeature.selectCurrent);
  public isPortingBlocked = this.store.selectSignal(userFeature.selectIsPortingBlocked);
  public showInstallationStatus = this.store.selectSignal(userFeature.selectShowInstallationStatus);

  public isSubscriptionPage = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url === `/${AppRouting.Products}/${AppRouting.ChangeSubscription}`)
    ),
    { initialValue: false }
  );

  public isStickyFromStore = this.store.selectSignal(
    subscriptionFeature.selectIsStickyElementVisible
  );

  public shouldShowSticky = computed(() => this.isSubscriptionPage() && this.isStickyFromStore());

  public isMobileMode = signal<boolean>(false);

  public message = computed(() => this.statusMessageService.message());
  public type = computed(() => this.statusMessageService.type());
  public StatusMessageType = StatusMessageType;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly modalService: ModalService,
    private readonly localeService: LocaleService,
    private readonly route: ActivatedRoute,
    private readonly statusMessageService: StatusMessageService
  ) { }

  public ngOnInit() {
    this.route.queryParams
      .pipe(
        untilDestroyed(this),
        tap((params) => {
          const { myapp, returnurl, token } = params;
          const hasAtLeastOneParam = !!(myapp || returnurl || token);
          this.isMobileMode.set(hasAtLeastOneParam);
        })
      )
      .subscribe();

    this.store
      .select(userFeature.selectIsActivationPending)
      .pipe(
        filter((isActivationPending) => isActivationPending),
        filter(() => !this.isMobileMode() && !this.isPortingBlocked()),
        switchMap(() => this.modalService.open(ActivateSimcardModalComponent)),
        filter((approved) => !!approved),
        tap(() => {
          this.router.navigate(['/', AppRouting.Settings, AppRouting.SimcardSettings], {
            state: { scrollToActivateSim: true },
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.store
      .select(userFeature.selectIsSoftBlocked)
      .pipe(
        filter(Boolean),
        switchMap(() => this.modalService.open(UnpaidInvoiceModalComponent)),
        filter((approved) => !!approved),
        tap(() => this.router.navigate(['/', AppRouting.Invoices])),
        untilDestroyed(this)
      )
      .subscribe();

    this.store
      .select(userFeature.selectIsPendingPayment)
      .pipe(
        filter((pendingPayment) => pendingPayment),
        tap(() => this.modalService.open(PendingPaymentModalComponent)),
        untilDestroyed(this)
      )
      .subscribe();

    this.route.queryParams
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        map((params) => params['locale']),
        concatLatestFrom(() => this.localeService.locales$),
        filter(([localeParam, locales]) => !!localeParam && locales.includes(localeParam)),
        tap(([localeParam, _]) => this.localeService.changeLocale(localeParam))
      )
      .subscribe();
  }

  public isChangeBankAccountPage() {
    return this.router.url.includes(AppRouting.ChangeBankAccountNumber);
  }

  public isSelectProductPage() {
    return this.router.url.includes(AppRouting.SelectProduct);
  }

  public isInstallationStatusPage() {
    return this.showInstallationStatus() && this.router.url.includes(AppRouting.Overview);
  }

  public isFullWidthPage() {
    return (
      this.isChangeBankAccountPage() ||
      this.isPortingBlocked() ||
      this.isSelectProductPage() ||
      this.isInstallationStatusPage()
    );
  }
}
